import {
  SELECT_THREAD,
  TOGGLE_NEW_THREAD,
  CANCEL_NEW_THREAD,
  PLACE_THREAD,
  SET_MODE,
  SHOW_RESOLVED_THREADS
} from '../actionTypes'
import { LOCATION_CHANGE } from 'react-router-redux'
import { firebaseAction } from '../utils'
import ReactGA from 'react-ga'
import i18n from 'i18n'
import { showNotification } from './alerts'
import { currentBoardIdSelector } from '../selectors/selectors'
import { newThreadSelector } from '../selectors/discussSelectors'
import { colWidthSelector } from '../selectors/boardSelectors'
import firebase from 'firebase/app'

const timestamp = firebase.database.ServerValue.TIMESTAMP

export const selectThread = threadId => {
  return {
    type: SELECT_THREAD,
    payload: { threadId }
  }
}

export const toggleResolvedThreads = show => {
  return {
    type: SHOW_RESOLVED_THREADS,
    payload: { show }
  }
}

export const toggleNewThread = () => ({ type: TOGGLE_NEW_THREAD })
export const cancelNewThread = () => ({ type: CANCEL_NEW_THREAD })
export const placeThread = placement => (dispatch, getState) => {
  // positions can be functions that need colWidth to compute value
  if (typeof placement.left !== typeof placement.top)
    console.error('both or neither should be functions')
  if (typeof placement.left === 'function') {
    const colWidth = colWidthSelector(getState())
    placement.left = placement.left(colWidth)
    placement.top = placement.top(colWidth)
  }

  const thread = {
    id: firebase.database()
      .ref()
      .push().key,
    new: true,
    ...placement
  }

  dispatch({
    type: PLACE_THREAD,
    payload: { thread }
  })
}

export const discussReducer = (
  state = { activeThread: null, showResolved: false },
  { type, payload }
) => {
  switch (type) {
    case SET_MODE:
      return { ...state, newThread: payload.mode === 'discuss' }
    case SELECT_THREAD:
      return {
        ...state,
        activeThread: payload.threadId,
        newThread: !!state.newThread
      }
    case TOGGLE_NEW_THREAD:
      return { ...state, newThread: !state.newThread, activeThread: null }
    case CANCEL_NEW_THREAD:
      return { ...state, newThread: true, activeThread: null }
    case PLACE_THREAD:
      return {
        ...state,
        newThread: payload.thread,
        activeThread: payload.thread.id
      }
    case SHOW_RESOLVED_THREADS:
      const { show = !state.showResolved } = payload
      return { ...state, showResolved: show }
    case LOCATION_CHANGE:
      const match = payload.hash.match(/^#t([\w-_]*)/)
      if (match) return { ...state, activeThread: match[1], newThread: null }
      break
    default:
  }
  return state
}

export const createThread = text =>
  firebaseAction(
    async (update, { getKey, getAuth, getState, dispatch }) => {
      const state = getState()
      const thread = newThreadSelector(state)
      const boardId = currentBoardIdSelector(state)
      const path = `/discuss/threads/${boardId}/${thread.id}`
      const userId = getAuth().uid
      const commentId = getKey()
      const forward = {
        [path]: {
          ...thread,
          new: null,
          boardId,
          comments: {
            [commentId]: {
              date: timestamp,
              id: commentId,
              text,
              userId
            }
          },
          date: timestamp
        },
        [`/discuss/viewedThreads/${userId}/${boardId}/${
          thread.id
        }/dateViewed/`]: timestamp
      }
      const backward = { [path]: null }
      await update({ forward, backward })

      dispatch(selectThread(thread.id))

      ReactGA.event({
        category: 'Discuss',
        action: 'New Thread'
      }, ['app'])
    }
  )

export const replyComment = (thread, text) =>
  firebaseAction(async (update, { getKey, getAuth }) => {
    const id = getKey()
    const path = `/discuss/threads/${thread.boardId}/${
      thread.id
    }/comments/${id}`
    const forward = {
      [path]: {
        id,
        date: timestamp,
        userId: getAuth().uid,
        text
      },
      [`/discuss/viewedThreads/${getAuth().uid}/${thread.boardId}/${
        thread.id
      }/dateViewed/`]: timestamp
    }
    const backward = { [path]: null }
    await update({ forward, backward })

    ReactGA.event({
      category: 'Discuss',
      action: 'Reply'
    }, ['app'])
  })

export const editComment = (comment, thread, text) =>
  firebaseAction(async update => {
    const path = `/discuss/threads/${thread.boardId}/${thread.id}/comments/${
      comment.id
    }/text`
    const forward = { [path]: text }
    const backward = { [path]: comment.text }
    await update({ forward, backward })

    ReactGA.event({
      category: 'Discuss',
      action: 'Edit Comment'
    }, ['app'])
  })

export const deleteComment = (comment, thread) =>
  firebaseAction((update, { dispatch }) => {

    // count non-deleted comments
    const commentCount = Object.values(thread.comments).filter(
      ({ dateDeleted }) => !dateDeleted
    ).length
    // only one comment left - delete thread
    if (commentCount === 1) {
      return dispatch(deleteThread(thread))
    }

    const path = `/discuss/threads/${thread.boardId}/${thread.id}/comments/${
      comment.id
    }/dateDeleted`
    const forward = { [path]: timestamp }
    const backward = { [path]: null }

    update({ forward, backward })

    dispatch(
      showNotification(i18n.t('discuss.comment.wasDeleted'), [
        {
          label: i18n.t('alert.undo'),
          action: (e, { dismiss }) => {
            update({ forward: backward })
            dismiss()
          }
        }
      ])
    )

    ReactGA.event({
      category: 'Discuss',
      action: 'Delete Comment'
    }, ['app'])
  })

export const markThreadRead = thread =>
  firebaseAction((update, { dispatch, getAuth }) => {
    const path = `/discuss/viewedThreads/${getAuth().uid}/${thread.boardId}/${
      thread.id
    }/dateViewed/`
    const forward = { [path]: timestamp }
    const backward = { [path]: null }
    update({ forward, backward })
  })

export const markThreadUnread = thread =>
  firebaseAction((update, { dispatch, getAuth }) => {
    const path = `/discuss/viewedThreads/${getAuth().uid}/${thread.boardId}/${
      thread.id
    }/dateViewed/`
    const forward = { [path]: null }
    const backward = { [path]: timestamp }
    update({ forward, backward })

    ReactGA.event({
      category: 'Discuss',
      action: 'Mark Thread Unread'
    }, ['app'])
  })

//`/discuss/viewedThreads/${userID}/${boardId}/${threadId}/dateViewed/`

export const deleteThread = thread =>
  firebaseAction((update, { dispatch }) => {
    const path = `/discuss/threads/${thread.boardId}/${thread.id}/dateDeleted`
    const forward = { [path]: timestamp }
    const backward = { [path]: null }

    dispatch(selectThread(null))

    update({ forward, backward })

    dispatch(
      showNotification(i18n.t('discuss.thread.wasDeleted'), [
        {
          label: i18n.t('alert.undo'),
          action: (e, { dismiss }) => {
            update({ forward: backward })
            dismiss()
          }
        }
      ])
    )

    ReactGA.event({
      category: 'Discuss',
      action: 'Delete Thread'
    }, ['app'])
  })

export const resolveThread = thread =>
  firebaseAction((update, { dispatch }) => {
    const path = `/discuss/threads/${thread.boardId}/${thread.id}/dateResolved`
    const forward = { [path]: timestamp }
    const backward = { [path]: null }
    update({ forward, backward })

    dispatch(
      showNotification(i18n.t('discuss.thread.wasResolved'), [
        {
          label: i18n.t('alert.undo'),
          action: (e, { dismiss }) => {
            update({ forward: backward })
            dismiss()
          }
        }
      ])
    )

    ReactGA.event({
      category: 'Discuss',
      action: 'Resolve Thread'
    }, ['app'])
  })

export const unresolveThread = thread =>
  firebaseAction(update => {
    const path = `/discuss/threads/${thread.boardId}/${thread.id}/dateResolved`
    const forward = { [path]: null }
    const backward = { [path]: thread.dateResolved }
    update({ forward, backward })

    ReactGA.event({
      category: 'Discuss',
      action: 'Unresolve Thread'
    }, ['app'])
  })
